








































































import { get, flatten, isFunction, compact, omit } from 'lodash';
import { ZenFormGroup } from 'zenComponents';
import ZenButton from '@reciprocity/button';
import { BFormRow, VBToggle, BCollapse, BSpinner } from 'bootstrap-vue';
import * as FieldPresenters from './field_presenters';
import { ObjectType, ObjectAttribute, FormField, FieldsGroup, FieldInformation } from '../types';

import {
  addFieldToSortedFieldGroup,
  createVuelidateObject,
  getErrorMessage,
  serializeFormData,
  hasPresenter,
} from '../utils';

import { DEFAULT_ERROR_MESSAGE, Layout } from '../constants';
import objectFormData from '../object_form_data';

const CUSTOM_ATTRIBUTES_GROUP = {
  name: 'additional',
  label: 'Additional',
  expanded: false,
};

interface ValidationData {
  ObjectType: {
    isValid: boolean | null;
    errorMessage: string;
  };
}

export default {
  name: 'DynamicForm',
  components: {
    ZenFormGroup,
    BFormRow,
    BCollapse,
    ZenButton,
    BSpinner,
    ...FieldPresenters,
  },
  directives: {
    BToggle: VBToggle,
  },
  props: {
    objectType: {
      type: String as () => ObjectType,
      required: true,
    },
    attributes: {
      type: Array as () => ObjectAttribute[],
      required: true,
    },
    form: {
      type: Object,
      default: () => {
        return {};
      },
    },
    isCreating: {
      type: Boolean,
      default: false,
    },
    settings: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  data() {
    return { formData: {}, collapsibleFormSections: {}, forcedFieldErrors: {} };
  },
  /**
   * We have to create an object that looks like this:
   * ```{
   *   title: { --> field name
   *     required,  --> validation
   *     minLength: minLength(4),  --> validation with arguments
   *   },
   *   code: { --> Other field validation...
   *     required,
   *   },
   * };```
   *
   * Fore more information, take a look at:
   * - How vuelidate works: https://vuelidate.netlify.com/#sub-basic-form
   * - Build-in validators: https://vuelidate.netlify.com/#sub-builtin-validators
   */
  validations() {
    return {
      formData: createVuelidateObject(this.formFieldArray),
    };
  },
  computed: {
    filteredAttributes() {
      const { attrMapper, attributes } = this;
      return attributes.filter((attr: ObjectAttribute): boolean => hasPresenter(attr, attrMapper));
    },
    attrMapper() {
      return get(objectFormData(this.objectType), 'attrMapper');
    },
    groupedAttrOrder() {
      return get(objectFormData(this.objectType), 'groupedAttrOrder');
    },
    groupedFields(): FieldsGroup[] {
      const { formData, shouldRender, createFieldBase, addFieldToAccumulator, attrMapper, filteredAttributes } = this;
      const groupedFields = filteredAttributes.reduce(
        (fieldsAccumulator: FieldsGroup[], attr: ObjectAttribute): FieldsGroup[] => {
          const mapperKey = attr.isCustomAttribute && attr.customAttributeType ? attr.customAttributeType : attr.name;
          const fieldInformation = attrMapper[mapperKey];
          const fieldBase: FormField = createFieldBase(attr, fieldInformation);
          if (shouldRender(fieldBase)) {
            return addFieldToAccumulator(fieldsAccumulator, fieldBase, mapperKey);
          } else {
            delete formData[mapperKey];
          }

          return fieldsAccumulator;
        },
        []
      );

      return Object.keys(groupedFields).map((groupIndex: string) => {
        const group: FieldsGroup = groupedFields[groupIndex];
        return {
          ...group,
          groupFields: compact(group.groupFields),
        };
      });
    },
    formFieldArray(): FormField[] {
      return flatten(this.groupedFields.map((fieldGroup: FieldsGroup) => fieldGroup.groupFields));
    },
    mappedFormFields(): Map<string, FormField> {
      return new Map(
        this.formFieldArray.map((formField: FormField): [string, FormField] => [formField.name, formField])
      );
    },
    // This computed property is used to pass to the field only if it is valid
    // or not and the error message. All the validation logic is managed in this
    // component. `forcedFieldErrors` contains the errors returned from the API
    // once the form is submitted.
    validationData(): ValidationData | null {
      const $formDataValidation = this.$v.formData;
      const forcedFieldErrors = this.forcedFieldErrors;
      return this.formFieldArray.reduce((validationData: ValidationData, field: FormField) => {
        if (forcedFieldErrors[field.name] !== undefined) {
          return {
            ...validationData,
            [field.name]: {
              isValid: false,
              errorMessage: this.forcedFieldErrors[field.name] || DEFAULT_ERROR_MESSAGE,
            },
          };
        }
        if (!$formDataValidation[field.name]) {
          return validationData;
        }
        const validationInfo = $formDataValidation[field.name];
        const { $dirty, $invalid, $pending } = validationInfo;

        const errorMessage = getErrorMessage(field.validation, validationInfo);

        return {
          ...validationData,
          [field.name]: {
            isValid: $dirty && !$pending ? !$invalid : null,
            errorMessage,
          },
        };
      }, {});
    },
    showButtons() {
      return this.settings.showButtons === null || this.settings.showButtons === undefined
        ? true
        : this.settings.showButtons;
    },
  },
  created() {
    this.collapsibleFormSections = this.groupedFields.reduce(
      (formSectionState: { [key: string]: boolean }, group: FieldsGroup) => ({
        ...formSectionState,
        [group.name]: group.expanded,
      }),
      {}
    );
  },
  methods: {
    get(object = {}, path: string, defaultValue: any) {
      return get(object, path, defaultValue);
    },
    createFieldBase(attributes: ObjectAttribute, fieldInformation: FieldInformation): FormField {
      const { overrideAttributes } = fieldInformation;
      const mapper = omit(fieldInformation, ['overrideAttributes']);
      return { ...attributes, mapper, ...overrideAttributes };
    },
    addFieldToAccumulator(fieldsAccumulator: FieldsGroup[], fieldBase: FormField, mapperKey: string): FieldsGroup[] {
      const { formData, groupedAttrOrder, $set, $store } = this;
      const extendedGroupedAttrOrder = groupedAttrOrder.concat(CUSTOM_ATTRIBUTES_GROUP);

      // Currently API is not returned pre-selected value, `fieldBase.value`
      // will always be undefined
      const initialValue = this.form[mapperKey] || get(fieldBase, 'mapper.extraInfo.initialValue', fieldBase.value);
      if (initialValue !== undefined && !formData[mapperKey]) {
        const value = typeof initialValue === 'function' ? initialValue($store.getters) : initialValue;
        $set(formData, mapperKey, value);
      }

      let sortedAttributeGroupIndex;
      if (fieldBase.isCustomAttribute) {
        sortedAttributeGroupIndex = extendedGroupedAttrOrder.findIndex(
          attrGroup => attrGroup.name === CUSTOM_ATTRIBUTES_GROUP.name
        );
      } else {
        sortedAttributeGroupIndex = groupedAttrOrder.findIndex(attrGroup =>
          attrGroup.attributes.includes(fieldBase.name)
        );
      }

      if (~sortedAttributeGroupIndex) {
        return addFieldToSortedFieldGroup(
          fieldsAccumulator,
          fieldBase,
          sortedAttributeGroupIndex,
          extendedGroupedAttrOrder
        );
      }

      return fieldsAccumulator;
    },
    valueChangedHandler(fieldKey: string, value: any) {
      this.$set(this.forcedFieldErrors, fieldKey, undefined);
      this.$set(this.formData, fieldKey, value);
      this.$v.formData[fieldKey].$touch();
      const triggerUpdates = get(this.mappedFormFields.get(fieldKey), 'mapper.triggerUpdates', false);
      if (triggerUpdates) {
        triggerUpdates({ formData: this.formData, updater: this.valueChangedHandler });
      }
    },
    toggleGroup(name: string) {
      this.collapsibleFormSections[name] = !this.collapsibleFormSections[name];
    },
    submitForm(keepModalOpened = false) {
      const $validations = this.$v;
      $validations.$touch();
      if (!$validations.$invalid) {
        const { regularAttributes, customAttributes } = serializeFormData(this.formData, this.mappedFormFields);
        this.$emit('createObject', { regularAttributes, customAttributes, keepModalOpened });
      } else {
        const firstFaillingField = this.formFieldArray.find((field: FormField) => {
          return $validations.formData[field.name].$invalid;
        });
        this.scrollToErrorField(firstFaillingField.name);
      }
    },
    getColumnClass(field: FormField): string {
      const customClass = this.get(field, 'mapper.renderInfo.customClass', Layout.Columns.Half.columnClass);
      return this.get(field, 'mapper.renderInfo.columnClass', customClass);
    },
    getFromGroupRefString(fieldName: string) {
      return `${fieldName}FormGroup`;
    },
    scrollToErrorField(fieldName: string) {
      const formGroupNode = get(this.$refs, `${fieldName}FormGroup[0].$el`);
      if (formGroupNode) {
        formGroupNode.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    setFieldError(fieldName: string, errorMessage: string) {
      this.$set(this.forcedFieldErrors, fieldName, errorMessage);
      this.scrollToErrorField(fieldName);
    },
    cleanForm() {
      this.formData = {};
      this.forcedFieldErrors = {};
      this.$v.$reset();
    },
    shouldRender(field: FormField): boolean {
      const conditionalRender = get(field, 'mapper.renderInfo.conditionalRender', () => true);
      return conditionalRender({ formData: this.formData });
    },
    getPresenterAttributes(field: FormField): any {
      const presenterAttributes = get(field, 'mapper.renderInfo.presenterAttributes');
      if (isFunction(presenterAttributes)) {
        return presenterAttributes({ field, formData: this.formData, objectType: this.objectType });
      }
      return presenterAttributes;
    },
    getNoLabelFieldValue(field: FormField): boolean {
      return get(field, 'mapper.renderInfo.noLabel', false);
    },
    getFieldComponentClass(field: FormField) {
      return get(field, 'mapper.renderInfo.presenterClass');
    },
    getPlaceholder(field: FormField) {
      return get(this.getPresenterAttributes(field), 'placeholder', field.placeholder);
    },
    formGroupRequired(field: FormField) {
      return get(field, 'mapper.renderInfo.formGroupAttributes.required', field.mandatory);
    },
  },
};
